import { Link, useLocation } from "wouter";
import { Home, Bell, FileText, Settings, LogOut, MessageCircle, Inbox, Sun, Moon, Sparkles, FolderDown, BookOpen } from "lucide-react";
import { useState, useEffect } from "react";
import InvestorChat from "./investor-chat";
import { CohortProvider } from "@/lib/cohort-context";
import { CohortSwitcher } from "@/components/cohort-switcher";
import { PortalSwitcher } from "@/components/portal-switcher";
import { useThemeMode } from "@/lib/use-theme-mode";

const BASE = import.meta.env.BASE_URL;

export default function InvestorPortalLayout({ children }: { children: React.ReactNode }) {
  const [location, navigate] = useLocation();
  const [investor, setInvestor] = useState<any>(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [researchUnread, setResearchUnread] = useState(false);
  const [chatInitialPrompt, setChatInitialPrompt] = useState<string | null>(null);

  // Allow any child page to open Kavya's chat with an optional pre-filled
  // prompt by dispatching a window CustomEvent. Avoids prop-drilling and
  // keeps the chat component itself unaware of the trigger source.
  useEffect(() => {
    const onOpenChat = (e: Event) => {
      const detail = (e as CustomEvent).detail || {};
      if (typeof detail.prompt === "string" && detail.prompt.trim()) {
        setChatInitialPrompt(detail.prompt.trim());
      }
      setChatOpen(true);
    };
    window.addEventListener("open-kavya-chat", onOpenChat as EventListener);
    return () => window.removeEventListener("open-kavya-chat", onOpenChat as EventListener);
  }, []);

  // Clear the prompt one tick after handing it off to the chat component.
  // Without this, if the user starts typing in the chat after a prompt is
  // pre-filled and then clicks a different prompt, the chat's effect fires
  // again and overwrites their typing — but only because `chatInitialPrompt`
  // changed. With this clear, the prompt is consumed once per dispatch:
  // a subsequent click sets a fresh non-null value, the chat consumes it,
  // and we go back to null. (Architect finding #17.)
  useEffect(() => {
    if (chatInitialPrompt === null) return;
    const t = setTimeout(() => setChatInitialPrompt(null), 80);
    return () => clearTimeout(t);
  }, [chatInitialPrompt]);
  const { mode: themeMode, toggle: toggleTheme } = useThemeMode("investor", "light");
  const ThemeIcon = themeMode === "dark" ? Sun : Moon;

  useEffect(() => {
    const data = localStorage.getItem("investor_data");
    if (data) {
      try { setInvestor(JSON.parse(data)); } catch {}
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("investor_token");
    if (!token) return;
    let cancelled = false;
    const fetchUnread = async () => {
      try {
        const r = await fetch(`${BASE}api/investor-portal/notifications/unread-count`, {
          headers: { "x-investor-token": token },
        });
        if (r.ok && !cancelled) {
          const d = await r.json();
          setUnreadCount(d.unread || 0);
        }
      } catch {}
    };
    fetchUnread();
    const iv = setInterval(fetchUnread, 30000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [location, chatOpen]);

  useEffect(() => {
    const token = localStorage.getItem("investor_token");
    if (!token) return;
    let cancelled = false;
    const fetchResearchSignal = async () => {
      try {
        const response = await fetch(`${BASE}api/investor-portal/research`, {
          headers: { "x-investor-token": token },
          credentials: "include",
        });
        if (!response.ok || cancelled) return;
        const result = await response.json();
        setResearchUnread(Array.isArray(result.data) && result.data.some(
          (item: { disclosureAccepted?: boolean }) => item.disclosureAccepted === false,
        ));
      } catch {
        // Research discovery is optional; access failures must not disrupt the shell.
      }
    };
    void fetchResearchSignal();
    return () => { cancelled = true; };
  }, [location]);

  const handleLogout = () => {
    // Clear per-investor "last visit" cursors before we drop investor_data,
    // so the next user on this device gets a fresh window and we don't
    // leak this investor's visit timestamps to anyone else.
    // (Architect finding #11.)
    try {
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && k.startsWith("investor_last_visit_at_")) keysToRemove.push(k);
      }
      keysToRemove.forEach(k => localStorage.removeItem(k));
    } catch {
      // localStorage unavailable (private mode, quota) — best-effort only.
    }
    localStorage.removeItem("investor_token");
    localStorage.removeItem("investor_data");
    localStorage.removeItem("investor_active_cohort");
    navigate("/investor/login");
  };

  const navItems = [
    { href: "/investor/portal", label: "Portfolio", icon: Home, exact: true },
    { href: "/investor/portal/inbox", label: "Inbox", icon: Inbox, badge: unreadCount },
    { href: "/investor/portal/rofr", label: "ROFR Center", icon: Bell },
    { href: "/investor/portal/curated", label: "Curated for you", icon: Sparkles },
    { href: "/investor/portal/research", label: "Research Pipeline", icon: BookOpen, dot: researchUnread },
    { href: "/investor/portal/documents", label: "Documents", icon: FileText },
    { href: "/investor/portal/files-from-banker", label: "Files from advisor", icon: FolderDown },
    { href: "/investor/portal/settings", label: "Settings", icon: Settings },
  ] as any[];

  return (
    <CohortProvider identity="investor">
    <div data-investor-theme={themeMode} className="flex min-h-screen min-w-0 w-full bg-[#0A0A0A] text-white flex-col md:flex-row">
      <aside className="w-full md:w-64 bg-[#111111] border-r border-white/[0.06] flex-shrink-0 flex flex-col hidden md:flex">
        <div className="p-6 border-b border-white/[0.06]">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-md bg-gradient-to-br from-[#FF5C00] to-[#E55200] flex items-center justify-center">
              <span className="text-white font-bold text-sm font-serif">F</span>
            </div>
            <div>
              <h1 className="text-lg font-sans tracking-tight font-bold text-white">Favcy Select</h1>
              <p className="text-white/30 text-[10px] font-mono uppercase tracking-[0.2em] leading-none">Investor Portal</p>
            </div>
          </div>
        </div>

        {investor && (
          <div className="px-4 py-3 border-b border-white/[0.06]">
            <p className="text-sm font-medium text-white/80 truncate">{investor.name}</p>
            <p className="text-[10px] text-white/30 truncate">{investor.email}</p>
          </div>
        )}

        <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
          {navItems.map((item) => {
            const isActive = item.exact
              ? location === item.href
              : location.startsWith(item.href);
            return (
              <Link
                key={item.href}
                href={item.href}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-[#FF5C00]/10 text-[#FF5C00]"
                    : "text-white/40 hover:bg-white/5 hover:text-white/70"
                }`}
              >
                <item.icon className="w-4 h-4" />
                <span className="flex-1">{item.label}</span>
                {item.badge > 0 && (
                  <span className="text-[10px] font-semibold bg-[#FF5C00] text-white rounded-full px-1.5 py-0.5 min-w-[18px] text-center">
                    {item.badge > 99 ? "99+" : item.badge}
                  </span>
                )}
                {item.dot && <span className="h-1.5 w-1.5 rounded-full bg-[#FF5C00]" aria-label="New research available" />}
              </Link>
            );
          })}
          <div className="my-2 border-t border-white/[0.06]" />
          <button
            onClick={() => setChatOpen(true)}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors w-full ${
              chatOpen
                ? "bg-[#FF5C00]/10 text-[#FF5C00]"
                : "text-white/40 hover:bg-white/5 hover:text-white/70"
            }`}
          >
            <MessageCircle className="w-4 h-4" />
            Ask Kavya
          </button>
        </nav>

        <div className="p-4 border-t border-white/[0.06]">
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 text-xs text-white/30 hover:text-white/60 transition-colors w-full px-3 py-2"
          >
            <LogOut className="w-3 h-3" /> Sign Out
          </button>
        </div>
      </aside>

      <div className="w-full min-w-0 bg-[#111111] border-b border-white/[0.06] md:hidden" data-testid="investor-mobile-header">
        <div className="flex min-w-0 items-center justify-between gap-3 p-4">
          <div className="flex min-w-0 items-center gap-2">
            <div className="w-7 h-7 shrink-0 rounded-md bg-gradient-to-br from-[#FF5C00] to-[#E55200] flex items-center justify-center">
              <span className="text-white font-bold text-xs font-serif">F</span>
            </div>
            <span className="truncate text-sm font-bold text-white">Favcy Select</span>
          </div>
          <div className="flex shrink-0 items-center">
            <button
              onClick={toggleTheme}
              title={`Switch to ${themeMode === "dark" ? "day" : "night"} mode`}
              aria-label="Toggle theme"
              className="p-2 text-white/40 hover:text-white/70"
            >
              <ThemeIcon className="w-4 h-4" />
            </button>
            <button onClick={handleLogout} aria-label="Sign out" className="p-2 text-white/30">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
        <div className="w-full min-w-0 overflow-x-auto border-t border-white/[0.06]" data-testid="investor-mobile-nav">
          <nav aria-label="Investor portal" className="flex w-max min-w-full items-center justify-around gap-1 px-2 py-1">
            {navItems.map((item) => {
              const isActive = item.exact ? location === item.href : location.startsWith(item.href);
              return (
                <Link
                  key={item.href}
                  href={item.href}
                  aria-label={item.label}
                  title={item.label}
                  className={`relative rounded-lg p-2 ${isActive ? "text-[#FF5C00]" : "text-white/40"}`}
                >
                  <item.icon className="w-4 h-4" />
                  {item.badge > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-[#FF5C00]" />
                  )}
                  {item.dot && <span className="absolute right-1 top-1 h-1.5 w-1.5 rounded-full bg-[#FF5C00]" />}
                </Link>
              );
            })}
            <button
              onClick={() => setChatOpen(true)}
              aria-label="Ask Kavya"
              title="Ask Kavya"
              className={`p-2 rounded-lg ${chatOpen ? "text-[#FF5C00]" : "text-white/40"}`}
            >
              <MessageCircle className="w-4 h-4" />
            </button>
          </nav>
        </div>
      </div>

      <main className="flex-1 flex w-full min-w-0 flex-col max-h-screen overflow-y-auto">
        <div className="px-6 md:px-10 pt-4 max-w-6xl w-full min-w-0 mx-auto flex items-center justify-end gap-2">
          <button
            onClick={toggleTheme}
            title={`Switch to ${themeMode === "dark" ? "day" : "night"} mode`}
            aria-label="Toggle theme"
            className="p-2 rounded-md text-white/50 hover:text-white/80 hover:bg-white/[0.06] transition-colors"
            data-testid="button-theme-toggle"
          >
            <ThemeIcon className="w-4 h-4" />
          </button>
          <CohortSwitcher tone="dark" />
        </div>
        <div className="flex-1 p-6 md:p-10 pt-4 max-w-6xl w-full min-w-0 mx-auto">
          {children}
        </div>
        <div className="border-t border-white/[0.06] py-5 mt-4">
          <PortalSwitcher current="investor" tone="dark" />
        </div>
      </main>

      <InvestorChat
        hideLauncher={/^\/investor\/portal\/research(?:\/|$)/.test(location)}
        externalOpen={chatOpen}
        onClose={() => { setChatOpen(false); setChatInitialPrompt(null); }}
        initialPrompt={chatInitialPrompt}
      />
    </div>
    </CohortProvider>
  );
}
